import { useMemo } from "react";
import { useSelector } from "react-redux";
import ProfileDropdown from "./ProfileDropdown";
import { OptimizedSvg } from "@components/common/OptimizedSvg";
import { userProfile } from "@tvet-mis/psda-assets";
import { useProfile } from "./services/GetProfile";
import isEmpty from "@utils/isEmpty";
import { capitalizeWords } from "@utils/string";
import { useAttachment } from "@hooks/useAttachment";
import type { RootState } from "@store/store";
import { CircularProgress } from "@mui/material";

const Profile = () => {
	const { data } = useProfile();
	const profilePicture = data?.profilePicture;
	const firstName = data?.firstName;
	const lastName = data?.lastName;

	const user = useSelector((state: RootState) => state.user.user);

	const roleLabel = useMemo(() => {
		if (user?.role) return user.role;
		return null;
	}, [user?.role]);

	const { url: profilePictureUrl, loading: isLoadingPicture } = useAttachment(profilePicture);

	const hasName = !isEmpty(firstName) || !isEmpty(lastName);
	const fullName = hasName ? `${firstName || ""} ${lastName || ""}`.trim() : "";
	const roleDisplay = roleLabel ? capitalizeWords(roleLabel) : null;

	const avatar = isLoadingPicture ? (
		<CircularProgress size={24} />
	) : profilePictureUrl ? (
		<img src={profilePictureUrl} alt="Profile" />
	) : (
		<OptimizedSvg
			src={userProfile}
			alt="user profile"
			className="header-profile-avatar__svg"
			width={38}
			height={38}
			cacheKey="user-profile"
		/>
	);

	return (
		<div className="header-profile-pill" aria-label={hasName ? fullName : "Profile menu"}>
			<div className="header-profile-avatar">{avatar}</div>
			{hasName && (
				<div className="header-profile-meta flex min-w-0 flex-col justify-center gap-0.5">
					<div
						className="max-w-[100px] truncate text-sm font-semibold leading-tight text-gray-900 md:max-w-[160px] lg:max-w-[130px]"
						title={fullName}>
						{fullName}
					</div>
					{roleDisplay && (
						<div
							className="max-w-[100px] truncate text-xs font-normal leading-tight text-gray-500 md:max-w-[160px] lg:max-w-[130px]"
							title={roleDisplay}>
							{roleDisplay}
						</div>
					)}
				</div>
			)}

			<div className="header-profile-chevron">
				<ProfileDropdown />
			</div>
		</div>
	);
};

export default Profile;
